import React, { useContext, useState } from 'react'
import moment from 'moment'
import { TreatmentLocationContext } from './TreatmentCentre'
import {
  Box,
  Button,
  Alert,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Close, OpenInNew, Search, SwapHoriz } from '@mui/icons-material'
import API from '../../../services/API'

const ZoneDrawer = ({ zone, open, onClose, onOpenPatient }) => {
  const [search, setSearch] = useState('')
  const [movingPatientID, setMovingPatientID] = useState(null)
  const [selectedMoveZone, setSelectedMoveZone] = useState('')
  const [moving, setMoving] = useState(false)
  const { treatmentZones = [], refresh } = useContext(TreatmentLocationContext)
  const patientCount = zone?.patients?.length || 0
  const capacity = Math.max(Number(zone?.capacity || zone?.maxCapacity) || 1, 1)
  const hasCapacity = patientCount < capacity

  const zoneCapacity = (item) =>
    Math.max(Number(item?.capacity || item?.maxCapacity) || 1, 1)

  const canMoveToZone = (item) =>
    String(item.id || item._id) !== String(zone?.id || zone?._id) &&
    (item.patients?.length || 0) < zoneCapacity(item)

  const startMove = (patient) => {
    setMovingPatientID(patient.id)
    setSelectedMoveZone('')
  }

  const movePatient = async (patient) => {
    if (!selectedMoveZone || moving) return

    const targetZone = treatmentZones.find(
      (item) => String(item.id || item._id) === String(selectedMoveZone),
    )

    setMoving(true)

    try {
      const res = await API.post(
        `/commandcontrol/treatment-assignments/${patient.id}`,
        {
          treatmentZone: selectedMoveZone,
          status:
            targetZone?.type === 'Waiting Room' || targetZone?.type === 'Waiting'
              ? 'Waiting'
              : 'In Treatment',
        },
      )

      if (res.status === 200) {
        setMovingPatientID(null)
        setSelectedMoveZone('')
        refresh?.()
        onClose?.()
      }
    } finally {
      setMoving(false)
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 520 }, height: '100%' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: 2,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
            color: 'white',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900} color="white">
              {zone?.name}
            </Typography>
            <Typography variant="caption" color="white" sx={{ opacity: 0.72 }}>
              {zone?.type} • {zone?.status} • {patientCount}/{capacity}
            </Typography>
          </Box>

          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Stack>

        <Stack spacing={2} sx={{ p: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(15,23,42,0.08)',
              backgroundColor: '#F8FAFC',
            }}
          >
            <Typography fontWeight={900}>Current Patients</Typography>

            <Stack spacing={1} sx={{ mt: 1 }}>
              {zone?.patients?.length ? (
                zone.patients.map((patient) => (
                  <Paper
                    key={patient.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: '1px solid rgba(15,23,42,0.08)',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    <Stack spacing={1}>
                      <Box>
                        <Typography fontWeight={900}>{patient.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Incident {patient.incidentNumber} • {patient.complaint}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Arrived {moment(patient.arrivedAt).fromNow()} • ePCR:{' '}
                          {patient.epcrStatus}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<OpenInNew />}
                          disabled={!patient.epcrID}
                          onClick={() => onOpenPatient?.(patient)}
                        >
                          Open ePCR
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SwapHoriz />}
                          onClick={() => startMove(patient)}
                        >
                          Move / Assign
                        </Button>
                      </Stack>

                      {movingPatientID === patient.id && (
                        <Stack spacing={1}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Move to</InputLabel>
                            <Select
                              label="Move to"
                              value={selectedMoveZone}
                              onChange={(e) =>
                                setSelectedMoveZone(e.target.value)
                              }
                            >
                              {treatmentZones.map((item) => {
                                const zoneID = item.id || item._id
                                const full = !canMoveToZone(item)

                                return (
                                  <MenuItem
                                    key={zoneID}
                                    value={zoneID}
                                    disabled={full}
                                  >
                                    {item.name} ({item.type}){' '}
                                    {String(zoneID) ===
                                    String(zone?.id || zone?._id)
                                      ? '- current'
                                      : full
                                        ? '- full'
                                        : ''}
                                  </MenuItem>
                                )
                              })}
                            </Select>
                          </FormControl>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!selectedMoveZone || moving}
                              onClick={() => movePatient(patient)}
                            >
                              {moving ? 'Moving...' : 'Confirm Move'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setMovingPatientID(null)
                                setSelectedMoveZone('')
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No patients currently assigned.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Divider />

          {hasCapacity ? (
            <>
              <Typography fontWeight={900}>Assign Patient</Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button fullWidth variant="contained" startIcon={<Add />}>
                  Create New Patient
                </Button>
                <Button fullWidth variant="outlined">
                  Link Existing
                </Button>
              </Stack>

              <TextField
                label="Search existing patients"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack spacing={1}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px dashed rgba(15,23,42,0.16)',
                    backgroundColor: '#F8FAFC',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    ePCR search and transfer will show real patient records here.
                  </Typography>
                </Paper>
              </Stack>
            </>
          ) : (
            <Alert severity="info">
              This space is at capacity. Move, transfer, or discharge the current
              patient before assigning another patient here.
            </Alert>
          )}
        </Stack>
      </Box>
    </Drawer>
  )
}

export default ZoneDrawer
